// client/src/components/influencer/InfluencerSidebar.js
// config 기반 사이드바 — 마케터 확정 패턴(MarketerSidebar) 미러, mb-* 클래스 재사용(ib-theme 민트)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/marketer/MarketerSidebar.css';

// SVG Icons
const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

// 그룹 구성 v4 시안 확정(2026-08-05): 프로젝트 → 포인트 → 내 정보 → 고객센터
const MENU_GROUPS = [
  {
    key: 'project',
    label: '프로젝트 (Projects)',
    items: [
      ['projectApply', '프로젝트 지원하기 (Apply to Projects)'],
      ['projectApplyCheck', '지원 중인 프로젝트 (My Applications)'],
      ['projectContract', '선정된 프로젝트 계약 (Selected Contracts)'],
      ['resultSend', '결과물 제출하기 (Submit Deliverables)'],
    ],
  },
  {
    key: 'point',
    label: '포인트 (Points)',
    items: [
      ['pointList', '포인트 내역 보기 (Point History)'],
      ['pointExchange', '포인트 환전 신청 (Request Withdrawal)'],
    ],
  },
  {
    key: 'my',
    label: '내 정보 (My Info)',
    items: [
      ['snsView', 'SNS 계정 · 단가 (SNS Accounts & Rates)'],
      ['snsLink', 'SNS 계정 연동 (Connect SNS)'],
      ['snsAdd', 'SNS 계정 추가 (Add SNS Account)'],
      ['bank', '통장 등록 · 변경 (Bank Account)'],
      ['contract', '프리랜서 계약 (Freelance Contract)'],
      ['profile', '내 정보 변경 (Edit Profile)'],
      ['password', '비밀번호 변경 (Change Password)'],
    ],
  },
  {
    key: 'inquiry',
    label: '고객센터 (Support)',
    items: [
      ['inquiryCheck', '진행 중인 문의 (Open Inquiries)'],
      ['inquiryFinish', '완료된 문의 (Closed Inquiries)'],
    ],
  },
];

// sub → 소속 그룹 (URL만 보고 열림 상태 동기화)
const SUB_TO_GROUP = MENU_GROUPS.reduce((acc, g) => {
  g.items.forEach(([sub]) => { acc[sub] = g.key; });
  return acc;
}, {});

const InfluencerSidebar = ({ isOpen, onClose, showSnsBadge }) => {
  const nav = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null);

  // URL 파라미터 기반 열림 상태 동기화
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const group = SUB_TO_GROUP[params.get('sub')];
    if (group) setOpenSection(group);
  }, [location.search]);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const closeOnMobile = () => {
    if (window.innerWidth <= 1024 && onClose) {
      onClose();
    }
  };

  const handleMenuClick = (sub) => {
    nav(`/influencer/mybiz?sub=${sub}`);
    closeOnMobile();
  };

  const params = new URLSearchParams(location.search);
  const currentSub = params.get('sub');
  const isActive = (sub) => currentSub === sub;
  const isHomeActive = !currentSub || currentSub === 'home';

  return (
    <div className={`mb-sidebar-area ${isOpen ? 'open' : ''}`}>
      <div className="mb-sidebar">
        {/* 프로필 영역 */}
        <div className="mb-sidebar-profile">
          <h3>인플루언서 센터 (Influencer Center)</h3>
          <p>Influencer Workspace</p>
        </div>

        {/* 네비게이션 */}
        <nav className="mb-sidebar-nav">

          {/* 활동 홈 */}
          <div className="mb-nav-group">
            <button
              className={`mb-menu-item mb-menu-item--single ${isHomeActive ? 'active' : ''}`}
              onClick={() => handleMenuClick('home')}
            >
              <span>활동 홈 (Activity Home)</span>
            </button>
          </div>

          {/* 접이식 그룹들 */}
          {MENU_GROUPS.map((group) => (
            <div className="mb-nav-group" key={group.key}>
              <button
                className={`mb-menu-item ${openSection === group.key ? 'active' : ''}`}
                onClick={() => toggleSection(group.key)}
              >
                <span>{group.label}</span>
                <ChevronDown className={`mb-menu-chevron ${openSection === group.key ? 'rotate' : ''}`} />
              </button>
              <div className={`mb-submenu ${openSection === group.key ? 'open' : ''}`}>
                <div className="mb-submenu__inner">
                  {/* 고객센터: 문의하기(작성)가 맨 위 */}
                  {group.key === 'inquiry' && (
                    <button
                      className="mb-submenu-item"
                      onClick={() => {
                        nav('/inquiry', { state: { role: 'influencer' } });
                        closeOnMobile();
                      }}
                    >
                      문의하기 (Contact Us)
                    </button>
                  )}
                  {group.items.map(([sub, label]) => (
                    <button
                      key={sub}
                      className={`mb-submenu-item ${sub === 'snsView' ? 'mb-submenu-item--flex' : ''} ${isActive(sub) ? 'active' : ''}`}
                      onClick={() => handleMenuClick(sub)}
                    >
                      {label}
                      {sub === 'snsView' && showSnsBadge && (
                        <span className="ib-warn-badge" title="희망단가 미설정 (Rate not set)">단가 미입력 (No rate)</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* 서비스 안내 — 여백이 있으면 하단 고정(margin-top:auto), 위 그룹 확장 시엔 밀려나 스크롤로 접근 */}
          <div className="mb-nav-group mb-nav-group--bottom">
            <div className="mb-menu-item" style={{ cursor: 'default' }}>
              <span>서비스 안내 (Service Info)</span>
            </div>
            <div className="mb-submenu open">
              <div className="mb-submenu__inner">
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/lp/influencer');
                    closeOnMobile();
                  }}
                >
                  인플루언서 안내 (For Influencers)
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
                >
                  서비스 가이드 (Service Guide) ↗
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/faq');
                    closeOnMobile();
                  }}
                >
                  FAQ
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default InfluencerSidebar;
